import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  Link,
  Breadcrumbs,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import AppMenu from '../components/AppMenu';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import LoginModal from '../components/LoginModal';
import SignupModal from '../components/SignupModal';

/**
 * Acceptable Use Policy page component
 * 
 * Contains the acceptable use policy guidelines for the MyEventShop platform
 */
const AcceptableUsePolicy: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isSignupOpen, setIsSignupOpen] = useState(false);

  const handleSwitchToSignup = () => {
    setIsLoginOpen(false);
    setIsSignupOpen(true);
  };

  const handleSwitchToLogin = () => {
    setIsSignupOpen(false);
    setIsLoginOpen(true);
  };

  // Table of contents items
  const tableOfContents = [
    { title: '1. Be Honest & Fair', id: 'honest-fair' },
    { title: '2. Respect the Platform & Others', id: 'respect' },
    { title: '3. Content and Event Standards', id: 'content' },
    { title: '4. Data, Privacy & Commercial Activities', id: 'data-privacy' },
    { title: '5. Ticket Resale / Transfer Rules', id: 'resale' },
    { title: '6. What Happens if Someone Doesn\'t Follow the Guidelines', id: 'consequences' },
    { title: '7. How to Report a Problem', id: 'report' },
    { title: '8. Policy Updates', id: 'updates' }
  ];

  return (
    <Box>
      <AppMenu 
        onLoginClick={() => setIsLoginOpen(true)}
        onSignupClick={() => setIsSignupOpen(true)} 
        currentPath={location.pathname}
      />
      
      <Container maxWidth="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mt: 3, mb: 2 }}
        >
          <Link 
            underline="hover" 
            color="inherit" 
            onClick={() => navigate('/')}
            sx={{ cursor: 'pointer' }}
          >
            Home
          </Link>
          <Typography color="text.primary">Acceptable Use Policy</Typography>
        </Breadcrumbs>

        {/* Hero Section */}
        <Box
          sx={{
            py: { xs: 4, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography 
            variant="h4" 
            component="h1" 
            color="primary" 
            fontWeight="bold"
            gutterBottom
          >
            Acceptable Use Policy
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '800px', mb: 2 }}>
            Welcome to MyEventShop!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '800px' }}>
            We're glad you're here. We want everyone — event‑organisers, ticket‑buyers, attendees — to have a safe, fair and positive experience. These guidelines explain what's okay and what's not when using our platform, in simple terms. Please read this in conjunction with the <Link href="/terms" color="primary">Terms of Service</Link>.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '800px', mt: 2, fontWeight: 'medium' }}>
            By using MyEventShop, you agree to follow these guidelines.
          </Typography>
        </Box>

        {/* Main Content Container */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 8 }}>
          {/* Table of Contents */}
          <Box 
            component={Paper} 
            elevation={2}
            sx={{
              p: 3, 
              borderRadius: 2, 
              width: { xs: '100%', md: '300px' },
              flexShrink: 0,
              position: { xs: 'static', md: 'sticky' },
              top: '20px',
              alignSelf: 'flex-start',
              maxHeight: { xs: 'auto', md: 'calc(100vh - 40px)' },
              overflowY: { xs: 'visible', md: 'auto' }
            }}
          >
            <Typography variant="h6" fontWeight="bold" mb={2}>Table of Contents</Typography>
            <List sx={{ p: 0 }}>
              {tableOfContents.map((item) => (
                <ListItem 
                  key={item.id} 
                  disablePadding 
                  sx={{ mb: 1 }}
                >
                  <ListItemText 
                    primary={
                      <Link 
                        href={`#${item.id}`} 
                        underline="hover" 
                        color="text.primary"
                        sx={{ 
                          display: 'block',
                          fontSize: '0.9rem',
                          lineHeight: 1.4,
                        }}
                      >
                        {item.title}
                      </Link>
                    } 
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Policy Content */}
          <Box 
            component={Paper}
            elevation={2}
            sx={{ 
              p: { xs: 3, md: 4 }, 
              borderRadius: 2,
              flex: '1 1 auto',
            }}
          >
            <Box id="honest-fair" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                1. Be Honest & Fair
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Please don't list events or tickets that don't exist, mislead people about the event, seats, price, or location.</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Don't pretend to be someone you're not, or claim affiliations you don't have.</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">If you're organising an event, make sure you're authorised to do so</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">If you're selling tickets or transferring them, please follow the relevant rules and regulations</Typography>
                </ListItem>
              </List>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="respect" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                2. Respect the Platform & Others
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Don't use bots, scripts or automated tools to buy tickets in bulk unfairly or scrape data.</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Don't disrupt or interfere with how the platform works (for example by overloading it, hacking or bypassing technical rules).</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Keep your account secure — don't share access, or allow someone to use your account without your responsibility.</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">If you see something odd or questionable, let us know right away by sending an email to <Link href="mailto:admin@myeventshop.com" color="primary">admin@myeventshop.com</Link>.</Typography>
                </ListItem>
              </List>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="content" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                3. Content and Event Standards
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Make sure event details, images and descriptions are accurate, lawful, and you have the rights to use them.</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Please avoid content that is hateful, discriminatory, harassing, obscene, pornographic, or that exploits children or minors in any way or in violation of any laws.</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">If your event content involves health, safety or medical themes, please ensure you're not spreading unverified or unsafe advice.</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">If you're offering more than tickets (for example merchandise, services, raffles or sweepstakes) make sure that's clearly stated, lawful, and permitted by any relevant rules and regulations.</Typography>
                </ListItem>
              </List>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="data-privacy" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                4. Data, Privacy & Commercial Activities
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">If you collect attendee data (names, emails, etc), please use it only for the purpose you said it was for (e.g., event info, updates) and not for unrelated spam or marketing unless you've got consent.</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Don't use the platform's payment or ticketing features to run contests, pyramid schemes, or unauthorised commercial activities.</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">If you sell tickets or transfer them, follow any pricing, resale or transfer rules we set (e.g., maximum markup, verified channels).</Typography>
                </ListItem>
              </List>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="resale" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                5. Ticket Resale / Transfer Rules
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">If resale or transfer is allowed, it must follow our rules (e.g., you may not resell above a certain price, or via unauthorised sites).</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Tickets that are resold or transferred in violation of our rules may be cancelled or invalidated.</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Using multiple accounts or disguising identity to get around purchase, transfer or pricing limits is not allowed.</Typography>
                </ListItem>
              </List>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="consequences" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                6. What Happens if Someone Doesn't Follow the Guidelines
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">We may edit, remove or cancel event listings or tickets if they break these guidelines.</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">We may suspend or permanently close accounts.</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">If there's a serious risk to safety, legality or fairness, we may take further action (including involving authorities).</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">In many cases we may not refund event fees or ticket sales if the event or listing is removed because of a breach.</Typography>
                </ListItem>
              </List>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="report" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                7. How to Report a Problem
              </Typography>
              <Typography variant="body1" paragraph>
                If you see something you believe violates these guidelines, please contact us at <Link href="mailto:admin@myeventshop.com" color="primary">admin@myeventshop.com</Link> with as much detail as you can.
              </Typography>
              <Typography variant="body1" paragraph>
                We'll review it and take action where needed.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="updates" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                8. Policy Updates
              </Typography>
              <Typography variant="body1" paragraph>
                We may update these guidelines from time to time — the latest version will always be on our site.
              </Typography>
              <Typography variant="body1" paragraph>
                When we make big changes, we'll let you know. Continuing to use the platform means you accept the updated version.
              </Typography>
            </Box>

            {/* Closing Message */}
            <Box sx={{ mt: 4, p: 3, bgcolor: 'rgba(95, 60, 111, 0.05)', borderRadius: 2 }}>
              <Typography variant="body1" sx={{ fontStyle: 'italic', textAlign: 'center' }}>
                Thanks for helping us keep MyEventShop safe, fair, and enjoyable for everyone!
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
      
      {/* Authentication Modals */}
      <LoginModal 
        open={isLoginOpen} 
        onClose={() => setIsLoginOpen(false)} 
        onSwitchToSignup={handleSwitchToSignup}
      />
      <SignupModal 
        open={isSignupOpen} 
        onClose={() => setIsSignupOpen(false)} 
        onSwitchToLogin={handleSwitchToLogin}
      />
    </Box>
  );
};

export default AcceptableUsePolicy;

